// tutorialOverlay.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';


const clamp = (v,a,b)=>Math.max(a,Math.min(v,b));
const wait  = (ms)=>new Promise(r=>setTimeout(r,ms));

/* ---------- Robust element resolution helpers ---------- */

/** climb to a visible ancestor with real box (handles tiny/hidden toggles) */
function expandToVisibleBox(el, min=12){
  let cur = el;
  while (cur && cur !== document.body){
    const cs = getComputedStyle(cur);
    if (cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity || '1') > 0){
      const r = cur.getBoundingClientRect();
      if (r.width >= min && r.height >= min) return cur;
    }
    cur = cur.parentElement;
  }
  return el;
}

/** find by text (fallback for “Image Generation”, “Create slides”, etc.) */
function findByText(text, root=document){
  const needle = text.trim().toLowerCase();
  const all = Array.from(root.querySelectorAll('*'));
  for (const el of all){
    const t = (el.innerText || el.textContent || '').trim().toLowerCase();
    if (t && t.includes(needle)) return el;
  }
  return null;
}

/** resolve a step to a visible anchor element */
async function resolveEl(step){
  const tryOnce = ()=>{
    const list = Array.isArray(step.target) ? step.target : [step.target];
    for (const sel of list){
      if (!sel) continue;
      const m = document.querySelector(sel);
      if (m){
        // Skip hidden elements (e.g. desktop vs mobile duplicates)
        if (getComputedStyle(m).display === 'none') continue;

        const el = expandToVisibleBox(m); 
        const r = el.getBoundingClientRect();
        if (r.width && r.height) return el;
      }
    }
    
    if (step.text) {
      const m = findByText(step.text);
      if (m) {
        const el = expandToVisibleBox(m);
        const r = el.getBoundingClientRect();
        if (r.width && r.height) return el;
      }
    }
    return null;
  };

  let el = tryOnce();
  if (el || !step.waitFor) return el;

  const deadline = performance.now() + step.waitFor;
  while (!el && performance.now() < deadline){
    await wait(100);
    el = tryOnce();
  }
  return el;
}

/* ---------- Default steps (tolerant selectors) ---------- */
function defaultSteps(){
  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    return [
      { target: ['[data-tour="mobile-models-btn"]'],
        text:'Models',
        title:'Model Selection', content:'Tap here to change models and filter by capability.', placement:'top' },
      { target: ['[data-tour="mobile-sysprompt-btn"]'],
        text:'System Prompt',
        title:'System Prompt', content:'Tap to set high-level instructions for the assistant.', placement:'top' },
      { target: ['[data-tour="prompt-input"]'],
        text:'Ask anything',
        title:'Message Box', content:'Type your message here.', placement:'top' },
      { target: ['[data-tour="more-tools"]'],
        text:'More Tools',
        title:'More Tools', content:'Access additional features like Document Upload, Audio Transcription, and PDF to PPTX summary.', placement:'top' },
      { target: ['[data-tour="image-generation-toggle"]', 'button[aria-label*="image generation"]'],
        text:'Image Generation',
        title:'Image Generation', content:'Enable/disable image generation.', placement:'top' },
      { target: ['[data-tour="clear"]', 'button[aria-label="Clear Conversation"]'],
        text:'Clear Conversation',
        title:'Clear Chat', content:'Reset the conversation.', placement:'top' },
      { target: ['[data-tour="send"]', 'button[aria-label="Send"]'],
        text:'Send',
        title:'Send', content:'Send your prompt to the model.', placement:'top' },
    ];
  }

  return [
    { target: ['[data-tour="model-select"]', '[data-sidebar]'],
      text:'Current model',
      title:'Model Selection', content:'Choose which LLM responds.', placement:'right' },
    { target: ['[data-tour="model-search"]'],
      text:'Filter Models',
      title:'Search Models', content:'Quickly find a specific model by name.', placement:'right' },
    { target: ['[data-tour="system-prompt"]'],
      text:'System Prompt',
      title:'System Prompt', content:'Set high-level instructions for the assistant.', placement:'bottom' },
    { target: ['[data-tour="prompt-input"]'],
      text:'Ask anything',
      title:'Message Box', content:'Type your message here.', placement:'top' },
    { target: ['[data-tour="clear"]'],
      text:'Clear Conversation',
      title:'Clear Chat', content:'Reset the conversation.', placement:'top' },
    { target: ['[data-tour="more-tools"]'],
      text:'More Tools',
      title:'More Tools', content:'Access additional features like Document Upload, Audio Transcription, and PDF to PPTX summary.', placement:'top' },
    { target: ['[data-tour="image-generation-toggle"]'],
      text:'Image Generation',
      title:'Image Generation', content:'Enable/disable image generation.', placement:'top' },
    { target: ['[data-tour="send"]'],
      text:'Send',
      title:'Send', content:'Send your prompt to the model.', placement:'top' },
  ];
}

const CSS = `
.tour-root { position: fixed; inset: 0; z-index: 99999; pointer-events: auto; font-family: sans-serif; }
.tour-scrim { position: absolute; background: rgba(0,0,0,0.5); transition: all 0.3s ease; }
.tour-ring { position: absolute; box-shadow: 0 0 0 9999px rgba(0,0,0,0.5); border-radius: 6px; transition: all 0.3s ease; pointer-events: none; }
.tour-bubble {
  position: absolute; width: 320px; background: #1f1f1f; color: #fff;
  border: 1px solid #333; border-radius: 12px; padding: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
  transition: left 0.3s ease, top 0.3s ease;
  display: flex; flex-direction: column; gap: 8px;
}
.tour-bubble::after {
  content: ""; position: absolute; width: 14px; height: 14px;
  background: #1f1f1f; border: 1px solid #333;
  transform: rotate(45deg);
}
/* Arrow positioning using variables, fallback to center */
.tour-bubble[data-place="top"]::after {
  bottom: -7px; 
  left: var(--tour-arrow-x, 50%); 
  border-top: 0; border-left: 0;
  margin-left: -7px;
}
.tour-bubble[data-place="bottom"]::after {
  top: -7px; 
  left: var(--tour-arrow-x, 50%); 
  border-bottom: 0; border-right: 0;
  margin-left: -7px;
}
.tour-bubble[data-place="left"]::after {
  right: -7px; 
  top: var(--tour-arrow-y, 50%); 
  border-bottom: 0; border-left: 0;
  margin-top: -7px;
}
.tour-bubble[data-place="right"]::after {
  left: -7px; 
  top: var(--tour-arrow-y, 50%); 
  border-top: 0; border-right: 0;
  margin-top: -7px;
}

.tour-title { margin: 0; font-size: 16px; font-weight: 600; }
.tour-body { margin: 0; font-size: 14px; color: #ccc; line-height: 1.4; }
.tour-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
.tour-progress { font-size: 12px; color: #666; }
.tour-btn {
  background: transparent; color: white; border: none; padding: 6px 12px;
  font-size: 13px; font-weight: 500; cursor: pointer;
  text-decoration: underline; text-underline-offset: 4px;
}
.tour-btn:hover { opacity: 0.8; }
.tour-btn.is-hidden { visibility: hidden; }
`;

/* ---------- Component ---------- */
export default function TutorialOverlay({ isOpen, onClose, steps: stepsProp }){
  const [idx,setIdx] = useState(0);
  const [rect,setRect] = useState({top:-1,left:-1,width:0,height:0});
  const [place,setPlace] = useState('bottom');
  const bubbleRef = useRef(null);

  // styles once
  useEffect(()=>{
    if (document.querySelector('style[data-tour]')) return;
    const tag = document.createElement('style');
    tag.setAttribute('data-tour','true');
    tag.textContent = CSS;
    document.head.appendChild(tag);
  },[]);

  const steps = useMemo(()=> (Array.isArray(stepsProp)&&stepsProp.length?stepsProp:defaultSteps()), [stepsProp]);

  const PAD=6, GAP=10;

  const positionFor = (el, prefer='auto')=>{
    const r = el.getBoundingClientRect();
    setRect({ top:r.top-PAD, left:r.left-PAD, width:r.width+PAD*2, height:r.height+PAD*2 });

    const b = bubbleRef.current;
    const bw = b?.offsetWidth || 320;
    const bh = b?.offsetHeight || 160;

    let p = prefer;
    if (p==='auto'){
      const roomR = window.innerWidth - r.right;
      const roomL = r.left;
      const roomT = r.top;
      if (roomR > bw + GAP) p='right';
      else if (roomL > bw + GAP) p='left';
      else if (roomT > bh + GAP) p='top';
      else p='bottom';
    }

    let top,left;
    // Center the bubble relative to the target
    if (p==='right'){ 
      top = r.top + r.height/2 - bh/2; 
      left = r.right + GAP; 
    }
    else if (p==='left'){ 
      top = r.top + r.height/2 - bh/2; 
      left = r.left - bw - GAP; 
    }
    else if (p==='top'){ 
      top = r.top - bh - GAP; 
      left = r.left + r.width/2 - bw/2; 
    }
    else { // bottom
      top = r.bottom + GAP; 
      left = r.left + r.width/2 - bw/2; 
    }

    // Clamp to viewport
    left = clamp(left, 12, window.innerWidth-bw-12);
    top  = clamp(top,  12, window.innerHeight-bh-12);

    setPlace(p);
    
    const finalLeft = Math.round(left);
    const finalTop = Math.round(top);

    // Calculate arrow offset for CSS to use
    // Clamp so arrow doesn't detach from rounded corners (radius 12px)
    const arrowX = clamp((r.left + r.width/2) - finalLeft, 14, bw - 14);
    const arrowY = clamp((r.top + r.height/2) - finalTop, 14, bh - 14);

    if (b){ 
      b.style.left = `${finalLeft}px`; 
      b.style.top = `${finalTop}px`; 
      b.style.setProperty('--tour-arrow-x', `${Math.round(arrowX)}px`);
      b.style.setProperty('--tour-arrow-y', `${Math.round(arrowY)}px`);
    }
  };

  // Start: pick first resolvable step
  useEffect(()=>{
    if (!isOpen) return;
    (async ()=>{
      for (let k=0; k<steps.length; k++){
        const el = await resolveEl(steps[k]);
        if (el){ setIdx(k); positionFor(el, steps[k].placement||'auto'); break; }
      }
    })();
  },[isOpen, steps]);

  // Per-step: resolve+scroll+position; track while smooth scrolling
  useEffect(()=>{
    if (!isOpen) return;
    let ro=null, scrollHandler=null, raf=0, frames=0, alive=true;

    (async ()=>{
      const step = steps[idx]; if (!step) return;
      const el = await resolveEl(step);
      if (!el){ // skip to any next resolvable step
        for (let k=idx+1;k<steps.length;k++){ if (await resolveEl(steps[k])){ setIdx(k); return; } }
        for (let k=idx-1;k>=0;k--){ if (await resolveEl(steps[k])){ setIdx(k); return; } }
        onClose?.(); return;
      }

      // 1) immediate position (so it "spawns" correctly)
      positionFor(el, step.placement||'auto');

      // 2) smooth scroll to center, then track for a few frames (handles moving target)
      el.scrollIntoView({ block:'center', inline:'nearest', behavior:'smooth' });

      const track = ()=>{
        if (!alive) return;
        positionFor(el, step.placement||'auto');
        if (++frames < 20) raf = requestAnimationFrame(track); // ~300ms
      };
      raf = requestAnimationFrame(track);

      // 3) observers keep it pinned on resize / content shifts
      ro = new ResizeObserver(()=>positionFor(el, step.placement||'auto'));
      ro.observe(el);
      scrollHandler = ()=>positionFor(el, step.placement||'auto');
      window.addEventListener('scroll', scrollHandler, true);
      window.addEventListener('resize', scrollHandler);
    })();

    return ()=>{
      alive=false;
      try{ ro && ro.disconnect(); } catch {}
      if (scrollHandler){
        window.removeEventListener('scroll', scrollHandler, true);
        window.removeEventListener('resize', scrollHandler);
      }
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[idx, isOpen]);

  const go = async dir=>{
    const start = idx + dir;
    if (start < 0 || start >= steps.length){ onClose?.(); return; }
    for (let k=start; k>=0 && k<steps.length; k+=dir){
      if (await resolveEl(steps[k])){ setIdx(k); return; }
    }
    onClose?.();
  };

  if (!isOpen) return null;

  // scrim pieces around the spotlight
  const T = Math.max(0, rect.top);
  const L = Math.max(0, rect.left);
  const W = Math.max(0, rect.width);
  const H = Math.max(0, rect.height);
  const R = L + W;
  const B = T + H;

  return createPortal(
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Tutorial overlay" onClick={onClose}>
      <div className="tour-scrim" style={{ left:0, top:0, width:'100vw', height:T }} />
      <div className="tour-scrim" style={{ left:0, top:T, width:L, height:H }} />
      <div className="tour-scrim" style={{ left:R, top:T, right:0, height:H }} />
      <div className="tour-scrim" style={{ left:0, top:B, width:'100vw', bottom:0 }} />
      <div className="tour-ring"  style={{ top:T, left:L, width:W, height:H }} />

      <div
        ref={bubbleRef}
        className="tour-bubble"
        data-place={place}
        onClick={(e)=>e.stopPropagation()}
      >
        <h3 className="tour-title">{steps[idx]?.title || 'Heading'}</h3>
        <p className="tour-body">{steps[idx]?.content || ''}</p>
        <div className="tour-footer">
          <button
            className={`tour-btn ${idx === 0 ? 'is-hidden' : ''}`}
            onClick={()=>go(-1)}
            aria-hidden={idx === 0}
            tabIndex={idx === 0 ? -1 : 0}
          >
            Back
          </button>
          <div className="tour-progress">{idx+1}/{steps.length || 1}</div>
          <button className="tour-btn" onClick={()=>go(+1)}>{idx >= steps.length-1 ? 'Close' : 'Next'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
